/**
 * Common utility functions for handling API responses and errors
 */

/**
 * Extracts error messages from API error response
 * @param {Object} error - The error object from API call
 * @returns {string} Formatted error message with HTML line breaks
 */
export const extractErrorMessage = (error) => {
    let errorMessage = "An error occurred. Please try again.";
    
    if (error?.response?.data) {
        const responseData = error.response.data;
        
        if (responseData.errors && Array.isArray(responseData.errors)) {
            // Show all error messages from the errors array, each on a new line
            const errorMessages = responseData.errors.map(err => err.msg).join('<br/>');
            errorMessage = errorMessages;
        } else if (responseData.message) {
            errorMessage = responseData.message;
        }
    } else if (error?.message) {
        errorMessage = error.message;
    }
    
    return errorMessage;
};

/**
 * Handles API errors in a consistent way
 * @param {Object} error - The error object from API call
 * @param {Function} setErrorState - State setter function for error state
 * @param {Function} setLoadingState - State setter function for loading state (optional)
 */
export const handleApiError = (error, setErrorState, setLoadingState = null) => {
    const errorMessage = extractErrorMessage(error);
    
    if (setLoadingState) {
        setLoadingState(false);
    }
    
    if (setErrorState) {
        setErrorState(errorMessage);
    }
    
    // Still log to console for debugging
    console.error('API Error:', error);
};

/**
 * Handles successful API responses
 * @param {Object} response - The response object from API call
 * @param {Function} setLoadingState - State setter function for loading state (optional)
 * @param {Function} onSuccess - Callback function for success handling (optional)
 * @returns {Object} The response data
 */
export const handleApiSuccess = (response, setLoadingState = null, onSuccess = null) => {
    if (setLoadingState) {
        setLoadingState(false);
    }
    
    if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(response);
    }
    
    return response?.data || response;
};
